import { AchievementBadge } from './AchievementBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Flame, TrendingUp, Award, CalendarCheck, Heart } from 'lucide-react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  requirement_type: string;
  requirement_value: number;
  points: number;
}

interface EarnedAchievement {
  achievement_id: string;
  earned_at: string;
}

interface AchievementsGridProps {
  achievements: Achievement[];
  earnedAchievements: EarnedAchievement[];
  totalPoints: number;
  className?: string;
}

export function AchievementsGrid({
  achievements,
  earnedAchievements,
  totalPoints,
  className
}: AchievementsGridProps) {
  const earnedIds = new Set(earnedAchievements.map(ea => ea.achievement_id));
  const earnedMap = new Map(earnedAchievements.map(ea => [ea.achievement_id, ea.earned_at]));

  const categories = [
    { id: 'all', label: 'All', icon: Award },
    { id: 'milestones', label: 'Milestones', icon: Trophy },
    { id: 'streaks', label: 'Streaks', icon: Flame },
    { id: 'performance', label: 'PRs', icon: TrendingUp },
    { id: 'consistency', label: 'Consistency', icon: CalendarCheck },
    { id: 'dedication', label: 'Dedication', icon: Heart }
  ];

  const filterByCategory = (category: string) => {
    if (category === 'all') return achievements;
    // Handle both singular and plural category names for backwards compatibility
    return achievements.filter(a => 
      a.category === category || 
      a.category === category.replace(/s$/, '') ||
      a.category + 's' === category
    );
  };

  const earnedCount = earnedAchievements.length;
  const totalCount = achievements.length;

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5 text-warning" />
            Achievements
          </CardTitle>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">
              {earnedCount}/{totalCount} unlocked
            </span>
            <span className="font-bold text-primary">{totalPoints} pts</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all">
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 mb-4">
            {categories.map(cat => (
              <TabsTrigger key={cat.id} value={cat.id} className="text-xs">
                <cat.icon className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">{cat.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {categories.map(cat => (
            <TabsContent key={cat.id} value={cat.id}>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4">
                {filterByCategory(cat.id).map(achievement => (
                  <div key={achievement.id} className="flex flex-col items-center gap-1">
                    <AchievementBadge
                      name={achievement.name}
                      description={achievement.description}
                      icon={achievement.icon}
                      category={achievement.category}
                      points={achievement.points}
                      earned={earnedIds.has(achievement.id)}
                      earnedAt={earnedMap.get(achievement.id)}
                      size="md"
                    />
                    <span className="text-xs text-center text-muted-foreground truncate w-full">
                      {achievement.name}
                    </span>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
